import { useState } from 'react';
import { Box, Grid } from '@mui/material';
import { CasDisplayField } from 'src/compass/shared/components';
import formatNumberWithCommas from 'src/compass/shared/util/formatNumberWithCommas';
import formatDecimal from 'src/compass/shared/util/formatDecimal';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import ClaimNotesField from './ClaimNotesField';
import RvwUploadDialog from './RvwUploadDialog';

type Props = {
  data: any;
  onUpdated: Function;
};

export default function ClaimInfoTab({ data, onUpdated }: Props) {
  const [dialogRow, setDialogRow] = useState<any>(null);

  const sx = { textAlign: 'right' };

  return (
    <>
      <Grid container spacing={6}>
        <Grid item sx={sx}>
          <CasDisplayField label="Cost/Head" value={data?.costPerHead} />
          <CasDisplayField label="Cost/CWT" value={data?.costPerCWT} />
          <CasDisplayField
            label="Insured Value"
            value={formatNumberWithCommas(data?.insuredValue)}
          />
          <CasDisplayField label="Total Premium" value={formatNumberWithCommas(data?.basePremium)} />
          <CasDisplayField label="Subsidy" value={formatNumberWithCommas(data?.subsidy)} />
          <CasDisplayField
            label="Producer Premium"
            value={formatNumberWithCommas(data?.netPremium)}
          />
          <CasDisplayField label="Price Adjustment Factor" value={data?.priceAdjustmentFactor} />
        </Grid>
        <Grid item sx={sx}>
          <CasDisplayField
            label="Settlement Status"
            value={
              <Box sx={data?.settlementStatus === 'Prelim' ? { backgroundColor: 'yellow' } : {}}>
                {data?.settlementStatus}
              </Box>
            }
          />
          <CasDisplayField label="Settlement" value={data?.settlement} />
          <CasDisplayField label="Indemnity/CWT" value={data?.indemnityPerCWT} />
          <CasDisplayField label="Net Indemnity/CWT" value={data?.netIndemnityPerCWT} />
          <CasDisplayField
            label="Indemnity"
            value={data?.indemnity !== null ? formatDecimal(data?.indemnity) : ''}
          />
          <CasDisplayField
            label="Net Indemnity"
            value={data?.netIndemnity !== null ? formatDecimal(data.netIndemnity) : ''}
          />
          <CasDisplayField
            label="Net Performance"
            value={data?.netPerformance !== null ? formatDecimal(data?.netPerformance) : ''}
          />
        </Grid>
        <Grid item sx={sx}>
          <CasDisplayField label="Length" value={data?.length} />
          <CasDisplayField label="Share" value={data?.insuredShare} />
          <CasDisplayField label="Claim Due By" value={data?.claimDueByDate} />
        </Grid>
        <Grid item xs>
          <CasDisplayField
            label="Upload Requests"
            value={
              data?.uploadRequests.length === 0 ? (
                '(None)'
              ) : (
                <>
                  {data?.uploadRequests.map((request: any, index: number) => (
                    <Box key={index}>
                      <CasLink onClick={() => setDialogRow(request)}>
                        {formatDate(request.dateCreated, DateFormat.MDY)}{' '}
                      </CasLink>
                      by {request.userCreated}: {request.requestType} ({request.status})
                      {request.isClosed &&
                      request.userClosed &&
                      request.userCreated !== request.userClosed
                        ? ` by ${request.userClosed}`
                        : ''}
                    </Box>
                  ))}
                </>
              )
            }
          />
          <ClaimNotesField
            id={data.id}
            label="Claim Notes"
            url="Sce/Claims/ClaimNotes"
            value={data?.claimNotes}
            onUpdated={onUpdated}
          />
          <ClaimNotesField
            id={data.id}
            label="Internal Notes"
            url="Sce/Claims/InternalNotes"
            value={data?.internalNotes}
            onUpdated={onUpdated}
          />
          {data?.growerId ? (
            <ClaimNotesField
              id={data.growerId}
              label="Insured Notes"
              url="Sce/Claims/InsuredNotes"
              value={data?.insuredNotes}
              onUpdated={onUpdated}
            />
          ) : (
            <CasDisplayField
              label={<>Insured Notes: <i>Hudson Grower# not available for this endorsement</i></>}
              value=""
            />
          )}
        </Grid>
      </Grid>
      {dialogRow && (
        <RvwUploadDialog
          id={dialogRow.id}
          onClose={() => setDialogRow(null)}
          onUpdated={onUpdated}
          showMarkClosedBtn={!dialogRow.isClosed}
        />
      )}
    </>
  );
}
